import React from 'react';
import { addYears, isAfter } from 'date-fns';

import { AdornedInput, Adornment, DatePicker, DatePickerInput, IconCalendarOutline } from '@zillow/constellation';

import type { Lease, Section } from '../../../../models/index';
import { LEASE_TERMS } from '../../../../constants';
import { DATE_PICKER_FORMAT, DATE_PICKER_PLACEHOLDER } from '../../../../constants/stringConstants';
import { disablePastDates } from '../../../../constants/functionConstants';
import { FULLSTORY_IGNORE_RAGE_CLICKS } from '../../../../constants/analytics';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  lease: Lease;
  hideLegalText: boolean;
  restrictToOneYear?: boolean;
};

export default function LeaseTermsEndDate({
  section,
  setSectionValues,
  hideLegalText,
  restrictToOneYear,
}: Props): JSX.Element {
  function onEndDateChange(date: Date): void {
    setSectionValues({
      [LEASE_TERMS.FIELD_NAME.END_DATE]: date,
    });
    validateEndDate();
  }

  function validateEndDate(): void {
    const field = section.getField(LEASE_TERMS.FIELD_NAME.END_DATE);

    if (!hideLegalText) {
      const twoYearsAfterStart = addYears(getStartDate(), 2);
      const oneYearAfterStart = addYears(getStartDate(), 1);

      isAfter(getEndDate(), twoYearsAfterStart) && !restrictToOneYear
        ? field.setErrorMessage(LEASE_TERMS.ERROR_MESSAGE.END_DATE_PAST_TWO_YEARS)
        : isAfter(getEndDate(), oneYearAfterStart) && restrictToOneYear
          ? field.setErrorMessage(LEASE_TERMS.ERROR_MESSAGE.END_DATE_PAST_ONE_YEAR)
          : field.setErrorMessage(LEASE_TERMS.ERROR_MESSAGE.END_DATE_IN_PAST);
    } else {
      field.setErrorMessage(LEASE_TERMS.ERROR_MESSAGE.END_DATE_IN_PAST);
    }
  }

  function getStartDate(): string {
    return section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE);
  }

  function getEndDate(): string {
    return section.getFieldValue(LEASE_TERMS.FIELD_NAME.END_DATE);
  }

  return (
    <AdornedInput
      className={FULLSTORY_IGNORE_RAGE_CLICKS}
      input={
        <DatePickerInput
          placeholder={DATE_PICKER_PLACEHOLDER}
          value={getEndDate()}
          onChange={onEndDateChange}
          datepicker={<DatePicker dateFormat={DATE_PICKER_FORMAT} disabledDates={disablePastDates} />}
        />
      }
      rightAdornment={
        <Adornment>
          <IconCalendarOutline />
        </Adornment>
      }
    />
  );
}
